import { DateTime } from "luxon";
import moment from "moment";
import { EDateTypes } from "../enums/date-types";

export const formatDate = (selectDate: Date | string) => {
  return moment(selectDate).toISOString(true);
};

export const formatDateByZoneId = (
  date: Date | string,
  zoneId?: string,
): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  const targetZone = zoneId && zoneId.trim() !== "" ? zoneId : "Etc/UTC";
  return dt.setZone(targetZone, { keepLocalTime: true }).toISO() ?? "";
};

export const getMonthRangeToDisplay = (weekRange: Date[]) => {
  const startingMonthAndYear = {
    month: weekRange[0].getMonth(),
    year: weekRange[0].getFullYear(),
  };
  const endingMonthAndYear = {
    month: weekRange[6].getMonth(),
    year: weekRange[6].getFullYear(),
  };

  const momentStartOfTheWeek = moment(weekRange[0]);
  const momentEndOfTheWeek = moment(weekRange[6]);

  if (startingMonthAndYear.month === endingMonthAndYear.month) {
    return `${momentStartOfTheWeek.format("MMM - YYYY")}`;
  } else if (startingMonthAndYear.year !== endingMonthAndYear.year) {
    return `${momentStartOfTheWeek.format(
      "MMM - YYYY",
    )} - ${momentEndOfTheWeek.format("MMM - YYYY")}`;
  } else {
    return `${momentStartOfTheWeek.format("MMM")} - ${momentEndOfTheWeek.format(
      "MMM",
    )} ${momentStartOfTheWeek.format("YYYY")}`;
  }
};

export const getWeekRange = (date: Date, selectedDate: number): Date[] => {
  const dayOfWeek = date.getDay();

  let difference = dayOfWeek - 1;

  if (difference < 0) {
    difference = 6;
  }

  const dates: Date[] = [];
  const startOfWeek = new Date(selectedDate);
  startOfWeek.setDate(date.getDate() - difference);

  for (let i = 0; i < 7; i++) {
    const newDate = new Date(startOfWeek);
    dates.push(new Date(newDate.setDate(newDate.getDate() + i)));
  }

  return dates;
};

export const getFromDate = (selectedDate: Date, selectedDateType: string) => {
  switch (selectedDateType) {
    case EDateTypes.HOURLY:
      return moment(selectedDate).startOf("day").toISOString();
    case EDateTypes.DAILY:
      return moment(selectedDate).startOf("month").toISOString();
    case EDateTypes.MONTHLY:
      return moment(selectedDate).startOf("year").toISOString();
    default:
      return moment().subtract(10, "years").startOf("year").toISOString();
  }
};

export const getToDate = (selectedDate: Date, selectedDateType: string) => {
  const isCurrentDay = moment(selectedDate).isSame(moment(), "day");
  const isCurrentMonth = moment(selectedDate).isSame(moment(), "month");
  const isCurrentYear = moment(selectedDate).isSame(moment(), "year");

  switch (selectedDateType) {
    case EDateTypes.HOURLY:
      return isCurrentDay
        ? moment().toISOString()
        : moment(selectedDate).endOf("day").toISOString();
    case EDateTypes.DAILY:
      return isCurrentMonth
        ? moment().toISOString()
        : moment(selectedDate).endOf("month").toISOString();
    case EDateTypes.MONTHLY:
      return isCurrentYear
        ? moment().toISOString()
        : moment(selectedDate).endOf("year").toISOString();
    default:
      return moment().toISOString();
  }
};

export const getMaxFromDate = (endDate: Date, type: EDateTypes): Date => {
  switch (type) {
    case EDateTypes.DAILY:
      return moment(endDate).subtract(1, "day").startOf("day").toDate();
    case EDateTypes.MONTHLY:
      return moment(endDate).subtract(1, "month").endOf("month").toDate();
    case EDateTypes.YEARLY:
      return moment(endDate).subtract(1, "year").endOf("year").toDate();
    default:
      return moment(endDate).subtract(1, "day").startOf("day").toDate();
  }
};

export const getMinToDate = (startDate: Date, type: EDateTypes): Date => {
  switch (type) {
    case EDateTypes.DAILY:
      return moment(startDate).add(1, "day").startOf("day").toDate();
    case EDateTypes.MONTHLY:
      return moment(startDate).add(1, "month").startOf("month").toDate();
    case EDateTypes.YEARLY:
      return moment(startDate).add(1, "year").startOf("year").toDate();
    default:
      return moment(startDate).add(1, "day").startOf("day").toDate();
  }
};

export const isSameOrAfterRange = (
  date1: Date,
  date2: Date,
  type: EDateTypes,
): boolean => {
  switch (type) {
    case EDateTypes.DAILY:
      return moment(date1).isSameOrAfter(moment(date2), "day");
    case EDateTypes.MONTHLY:
      return moment(date1).isSameOrAfter(moment(date2), "month");
    case EDateTypes.YEARLY:
      return moment(date1).isSameOrAfter(moment(date2), "year");
    default:
      return moment(date1).isSameOrAfter(moment(date2), "day");
  }
};

export const isSameOrBeforeRange = (
  date1: Date,
  date2: Date,
  type: EDateTypes,
): boolean => {
  switch (type) {
    case EDateTypes.DAILY:
      return moment(date1).isSameOrBefore(moment(date2), "day");
    case EDateTypes.MONTHLY:
      return moment(date1).isSameOrBefore(moment(date2), "month");
    case EDateTypes.YEARLY:
      return moment(date1).isSameOrBefore(moment(date2), "year");
    default:
      return moment(date1).isSameOrBefore(moment(date2), "day");
  }
};

export const getDefaultDates = (type: EDateTypes) => {
  const now = new Date();
  switch (type) {
    case EDateTypes.DAILY: {
      const monthStart = moment().startOf("month").toDate();
      const start = moment(monthStart).isSameOrAfter(moment(now), "day")
        ? moment(now).subtract(1, "month").startOf("month").toDate()
        : monthStart;
      return { start, end: now };
    }
    case EDateTypes.MONTHLY: {
      const yearStart = moment().startOf("year").toDate();
      const start = moment(yearStart).isSameOrAfter(moment(now), "month")
        ? moment(now).subtract(1, "year").startOf("year").toDate()
        : yearStart;
      return { start, end: now };
    }
    case EDateTypes.YEARLY:
      return {
        start: moment().subtract(6, "years").startOf("year").toDate(),
        end: now,
      };
    default:
      return {
        start: now,
        end: now,
      };
  }
};
